// Typed API client for wireguard-manager backend

export interface Peer {
	ipAddress: string;
	publicKey: string | null;
	status: "unassigned" | "assigned" | "revoked";
	assignedTo: string | null;
	assignedBy: string | null;
	notes: string | null;
	createdAt: string;
	assignedAt: string | null;
	revokedAt: string | null;
}

export interface PoolStatus {
	total: number;
	unassigned: number;
	assigned: number;
	revoked: number;
}

export interface AuditEntry {
	id: number;
	ipAddress: string;
	action: string;
	actor: string;
	oldStatus: string | null;
	newStatus: string;
	reason: string | null;
	createdAt: string;
}

export interface UserInfo {
	sub: string;
	email: string;
	displayName: string;
	groups: string[];
	isAdmin: boolean;
}

const BASE = "";

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
	const resp = await fetch(`${BASE}${path}`, {
		...opts,
		headers: {
			"Content-Type": "application/json",
			...opts?.headers,
		},
	});

	if (resp.status === 401) {
		window.location.href = "/auth/login";
		throw new Error("Unauthorized");
	}

	if (!resp.ok) {
		const body = await resp.json().catch(() => ({ error: resp.statusText }));
		throw new Error(body.error || `HTTP ${resp.status}`);
	}

	return resp.json() as Promise<T>;
}

export const api = {
	getMe: () => request<UserInfo>("/auth/me"),

	listPeers: (params?: { status?: string; search?: string }) => {
		const qs = new URLSearchParams();
		if (params?.status) qs.set("status", params.status);
		if (params?.search) qs.set("search", params.search);
		const q = qs.toString();
		return request<{ peers: Peer[] }>(`/api/v1/peers${q ? `?${q}` : ""}`);
	},

	getPeer: (ip: string) => request<{ peer: Peer }>(`/api/v1/peers/${ip}`),

	assignPeer: (ip: string, assignedTo: string, notes?: string) =>
		request<{ peer: Peer }>(`/api/v1/peers/${ip}/assign`, {
			method: "POST",
			body: JSON.stringify({ assignedTo, notes }),
		}),

	revokePeer: (ip: string, reason?: string) =>
		request<{ peer: Peer }>(`/api/v1/peers/${ip}/revoke`, {
			method: "POST",
			body: JSON.stringify({ reason }),
		}),

	getPoolStatus: () => request<PoolStatus>("/api/v1/pool/status"),

	initializePool: () =>
		request<{ message: string; inserted: number; existing: number }>(
			"/api/v1/pool/initialize",
			{ method: "POST" },
		),

	getAuditLog: (params?: { ip?: string; limit?: number; offset?: number }) => {
		const qs = new URLSearchParams();
		if (params?.ip) qs.set("ip", params.ip);
		if (params?.limit) qs.set("limit", String(params.limit));
		if (params?.offset) qs.set("offset", String(params.offset));
		const q = qs.toString();
		return request<{ entries: AuditEntry[] }>(`/api/v1/audit${q ? `?${q}` : ""}`);
	},

	downloadConfig: (ip: string) => {
		// Direct download — browser handles Content-Disposition
		window.open(`/api/v1/peers/${ip}/config`, "_blank");
	},
};
